<script lang="ts" module>
	let scrollY = $state(0);

	export function getScrollY(): number {
		return scrollY;
	}
</script>

<script lang="ts">
	import AuthenticationMenu from "@components/AuthenticationMenu.svelte";
	import HeaderCartLink from "@components/HeaderCartLink.svelte";
	import Logo from "@components/Logo.svelte";
	import Toast from "@components/Toast.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount, tick } from "svelte";
	import { flip } from "svelte/animate";
	import { cubicIn, cubicOut } from "svelte/easing";
	import { fly } from "svelte/transition";
	import hamburgerIcon from "@core/assets/icons/hamburger.svg?raw";
	import logoutIcon from "@core/assets/icons/logout.svg?raw";
	import plusIcon from "@core/assets/icons/plus.svg?raw";
	import userIcon from "@core/assets/icons/user.svg?raw";
	import zoomIcon from "@core/assets/icons/zoom.svg?raw";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { getAccountDetailsPath } from "@core/schema/paths/getAccountDetailsPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { idFromAnchor } from "@core/schema/utils/idFromAnchor.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { oneTimeEffect } from "@core/utils/svelte/oneTimeEffect.svelte.js";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import { toasts } from "../../state/toasts.svelte.js";
	import AlgoliaSearchbar from "./AlgoliaSearchbar.svelte";
	import translations from "./Header.translations.json" with { type: "json" };
	import InfoBanner from "./InfoBanner.svelte";
	import MobileMenuLanguageSwitcher from "./MobileMenuLanguageSwitcher.svelte";

	const translate = getTranslate(translations);

	const { transparent: initialTransparent }: { transparent: boolean } = $props();
	const { menu, tenant, infoBannerMessages } = getGlobalTemplateProps();

	const signedIn = $derived(Auth.signedIn);

	let mobileMenuOpen = $state(false);
	let searchbarOpen = $state(false);
	let authMenuOpen = $state(false);

	let header = $state<HTMLElement>();

	async function signOut(): Promise<void> {
		await Auth.signOut();
		authMenuOpen = false;
	}

	const transparent = $derived(
		initialTransparent && scrollY < 16 && !mobileMenuOpen && !searchbarOpen && !authMenuOpen,
	);

	let mounted = $state(false);

	function openModalFromQuery(): void {
		const url = new URL(location.href);
		const modal = url.searchParams.get("modal");

		if (isEnum(AuthenticationModalKind)(modal)) {
			void Auth.openAuthenticationModal(modal, {
				searchParams: url.searchParams,
			});
		} else if (modal === "cart") {
			Cart.showModal = true;
		} else if (isEnum(SubscriptionKind)(modal)) {
			Cart.openSubscriptionConfigurator(modal);
		} else {
			url.searchParams.delete("modal");
			history.replaceState(null, "", url.toString());
		}
	}

	$effect(() => {
		if (!mounted) {
			return;
		}

		const url = new URL(location.href);

		if (Cart.showModal) {
			url.searchParams.set("modal", "cart");
			Auth.closeAuthenticationModal();
		} else if (Cart.showSubscriptionConfigurator && Cart.state?.subscription.kind !== undefined) {
			url.searchParams.set("modal", Cart.state.subscription.kind);
			Auth.closeAuthenticationModal();
		} else if (Auth.authenticationModalKind !== undefined) {
			url.searchParams.set("modal", Auth.authenticationModalKind);
		} else {
			url.searchParams.delete("modal");
		}

		history.replaceState(null, "", url.toString());
	});

	function handleWindowMouseup(event: MouseEvent): void {
		if (!(event.target instanceof Node) || !header?.contains(event.target)) {
			authMenuOpen = false;
		}
	}

	function scrollToHash(id: string): void {
		const element = document.getElementById(id);
		if (!element || !header) {
			return;
		}
		const top = element.getBoundingClientRect().top + window.scrollY - header.clientHeight;
		window.scrollTo({ top, behavior: "smooth" });
	}

	function handleHashChange(event?: HashChangeEvent): void {
		const id = idFromAnchor(location.hash);
		if (!id) {
			return;
		}
		event?.preventDefault();
		scrollToHash(id);
	}

	function handleDocumentClick(event: MouseEvent): void {
		if (!(event.target instanceof Element)) {
			return;
		}
		const anchor = event.target.closest("a");
		if (!anchor) {
			return;
		}
		const url = new URL(anchor.href, location.href);
		if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) {
			return;
		}
		const id = idFromAnchor(url.hash);
		if (!document.getElementById(id)) {
			return;
		}
		event.preventDefault();
		history.pushState(null, "", url.hash);
		scrollToHash(id);
	}

	onMount(() => {
		void tick().then(() => {
			handleHashChange();
		});
	});

	oneTimeEffect(
		() => Cart.loaded,
		() => {
			openModalFromQuery();
			mounted = true;
		},
	);

	const hasInfoBanner = $derived(scrollY < 1);
</script>

<svelte:document onclick={handleDocumentClick} />
<svelte:window
	onscroll={(event: UIEvent): void => {
		if (!lockScroll.locked && !event.defaultPrevented) {
			({ scrollY } = window);
		}
	}}
	onpopstate={openModalFromQuery}
	onmouseup={handleWindowMouseup}
	onhashchange={handleHashChange}
/>
<InfoBanner messages={infoBannerMessages} />
<header
	class={["flex flex-col items-stretch z-40 w-full", "sticky top-0", initialTransparent && "-mb-14 2lg:-mb-20"]}
	bind:this={header}
>
	<div
		class={[
			"h-14 2lg:h-20 flex items-center justify-center z-40 transition-colors duration-75 border-b relative",
			transparent ? "text-white border-transparent" : "bg-white border-b-fcrlightgray-300 text-fcrdarkbrown",
		]}
	>
		<div
			class="flex items-center w-full max-w-[105rem] justify-between 2lg:grid grid-cols-[1fr_auto_1fr] px-4 2lg:px-6"
		>
			<div class="flex items-center min-w-fit">
				<a href={getHomepagePath(tenant)} aria-label={translate("logoAria")}>
					<Logo
						class={[
							"w-28 h-6 mr-3 xl:w-30 xl:h-7 xl:mr-4",
							searchbarOpen && "hidden xs:flex",
							transparent ? "text-white" : "text-fcrdarkgray",
						]}
					/>
				</a>
				<AlgoliaSearchbar {transparent} bind:open={searchbarOpen} {hasInfoBanner} />
			</div>
			<nav
				class={[
					"hidden 2lg:flex gap-6 xl:gap-10 font-antipol justify-center items-center justify-self-center px-4 2lg:px-6",
					searchbarOpen && "absolute left-1/2 text-nowrap -translate-x-1/2",
				]}
			>
				{#each menu as item (item)}
					<a
						href={item.href}
						class="hover:text-fcrbrown transition-colors font-medium text-sm/tighter 2lg:text-base/tighter xl:text-base/tighter"
					>
						<Formatted text={item.name} />
					</a>
				{/each}
			</nav>
			{#if searchbarOpen}
				<div></div>
			{/if}
			<div class="flex gap-6 2lg:gap-2 shrink-0 2lg:justify-self-end relative">
				<button
					type="button"
					class={["size-6", searchbarOpen ? "hidden" : "2lg:hidden"]}
					title={translate("openSearchbar")}
					onclick={(): void => {
						searchbarOpen = !searchbarOpen;
					}}
				>
					<Icon icon={zoomIcon} class="size-6" />
				</button>
				<HeaderCartLink transparentHeader={transparent} class={[searchbarOpen && "hidden 2lg:flex"]} />
				<button
					type="button"
					class="size-6 2lg:hidden"
					onclick={(): void => {
						if (searchbarOpen) {
							searchbarOpen = false;
						} else {
							mobileMenuOpen = !mobileMenuOpen;
						}
					}}
					title={translate(mobileMenuOpen || searchbarOpen ? "closeMenu" : "openMenu")}
				>
					{#if mobileMenuOpen || searchbarOpen}
						<Icon icon={plusIcon} class="size-6 rotate-45" />
					{:else}
						<Icon icon={hamburgerIcon} class="size-6" />
					{/if}
				</button>
				{#if signedIn}
					<button
						type="button"
						class="bg-fcrbeige hover:border-fcrgray hover:border hidden rounded-full 2lg:flex items-center justify-center size-12 xl:size-14"
						onclick={(): void => {
							authMenuOpen = !authMenuOpen;
						}}
					>
						<Icon icon={userIcon} class="size-6 text-fcrbrown" />
					</button>
				{:else}
					<Button
						class="2lg:flex hidden transition-colors 2lg:h-12 xl:h-auto 2lg:text-sm xl:text-base text-nowrap"
						variant={transparent ? ButtonVariant.White : ButtonVariant.Dark}
						size={ButtonSize.Medium}
						onclick={async (): Promise<void> => {
							await Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, {});
						}}
					>
						<Formatted text={translate("login")} />
					</Button>
				{/if}
			</div>
		</div>
	</div>
	<div
		class={[
			"flex flex-col bg-white px-4 shadow-xl z-40 fixed w-full overflow-y-auto overscroll-contain",
			hasInfoBanner
				? "top-[calc(3.5rem+var(--info-banner-height))] xl:top-[calc(5rem+var(--info-banner-height))] max-h-[calc(100dvh-3.5rem-var(--info-banner-height))]"
				: "top-14 xl:top-20 max-h-[calc(100dvh-3.5rem)]",
			mobileMenuOpen ? "2lg:hidden" : "hidden",
		]}
	>
		{#each menu as item (item)}
			<a href={item.href} class="text-lg/tighter text-fcrdarkgray font-antipol py-3.5">
				<Formatted text={item.name} />
			</a>
			<hr class="border-t-fcrlightgray" />
		{/each}
		<div class="flex justify-between items-center">
			{#if signedIn}
				<a
					href={getAccountDetailsPath(tenant)}
					class="w-full grow shrink text-lg/tighter text-fcrdarkgray font-antipol py-3.5"
				>
					<Formatted text={translate("customerAccount")} />
				</a>
				<button
					type="button"
					class="border-l border-fcrlightgray flex items-center justify-center h-12 w-16 grow-0 shrink-0"
					aria-label={translate("logout")}
					onclick={signOut}
				>
					<Icon icon={logoutIcon} class="size-6" />
				</button>
			{:else}
				<button
					class="w-full grow shrink text-lg/tighter text-fcrdarkgray font-antipol py-3.5 text-left"
					onclick={async (): Promise<void> => {
						await Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, {});
					}}
				>
					<Formatted text={translate("login")} />
				</button>
			{/if}
		</div>
		<hr class="border-t-fcrlightgray sm:hidden" />
		<MobileMenuLanguageSwitcher />
	</div>
	{#if authMenuOpen}
		<AuthenticationMenu
			class={[
				"w-75 sm:ml-auto mr-2 mt-2 transition-[opacity] duration-300 fixed right-0 z-[121] hidden 2lg:flex",
				hasInfoBanner ? "top-[calc(5rem+var(--info-banner-height))]" : "top-20",
			]}
			onsignout={signOut}
		/>
		<button
			type="button"
			onclick={(): void => {
				authMenuOpen = false;
			}}
			aria-label={translate("closeMenu")}
			class={[
				"fixed  z-[39] 2lg:z-[120] inset-0 bg-fcrdarkgray/80 appearance-none cursor-auto",
				hasInfoBanner ? "top-[calc(5rem+var(--info-banner-height))]" : "top-20",
			]}
		></button>
	{/if}
</header>
<section
	class={[
		"fixed top-14 2lg:top-20 z-[100] grid w-full pointer-events-none px-6",
		hasInfoBanner ? "pt-[calc(0.5rem+var(--info-banner-height))]" : "pt-2",
	]}
>
	{#each toasts.items as toast (toast.id)}
		<div
			class="w-full *:pointer-events-auto flex justify-end col-start-1 row-start-1"
			animate:flip={{ duration: 300 }}
			in:fly={{ y: 40, duration: 300, easing: cubicOut }}
			out:fly={{ y: -40, duration: 200, easing: cubicIn }}
		>
			<Toast {toast} />
		</div>
	{/each}
</section>
